import { useState, useCallback, useRef } from 'react';

const EDGE_GRAB = 6;   // px grab zone for text-field edge resize
const FACE_HANDLE = 7;  // px grab zone for face resize/rotate handles
const SNAP_PX = 2;     // snap distance in preview pixels
const ROTATE_HANDLE_OFFSET = 24; // px distance of rotation handle from oval edge

/**
 * Canvas drag hook. Returns mouse handlers + alignment guides + cursor.
 *
 * onChange(formData)      – live preview update (every mousemove)
 * onCommit()             – called on mouseup to commit to undo history
 */
const useCanvasDrag = ({ formData, previewW, previewH, onChange, onCommit, zoom = 1 }) => {
  const [dragState, setDragState] = useState(null);
  const [hoveredElement, setHoveredElement] = useState(null);
  const [selectedElement, setSelectedElement] = useState(null);
  const [alignmentGuides, setAlignmentGuides] = useState([]);
  const dragRef = useRef(null);

  const pctToX = (pct) => (pct || 0) * previewW;
  const pctToY = (pct) => (pct || 0) * previewH;
  const xToPct = (px) => px / previewW;
  const yToPct = (px) => px / previewH;

  // Get face oval geometry
  const getFaceGeom = useCallback(() => {
    if (!formData.eyes_y && formData.eyes_y !== 0) return null;
    if (!formData.face_height && formData.face_height !== 0) return null;
    if (!formData.center_x && formData.center_x !== 0) return null;

    const cx = pctToX(formData.center_x);
    const ey = pctToY(formData.eyes_y);
    const fh = pctToY(formData.face_height);
    const ovalCY = ey + fh * 0.1;
    const rx = fh * 0.35;
    const ry = fh / 2;
    const rotation = (formData.rotation || 0) * Math.PI / 180;

    return { cx, ey, ovalCY, fh, rx, ry, rotation };
  }, [formData, previewW, previewH]);

  // Transform mouse coords into the face oval's local coordinate system
  const toLocalFace = (mx, my, geom) => {
    const dx = mx - geom.cx;
    const dy = my - geom.ovalCY;
    const cos = Math.cos(-geom.rotation);
    const sin = Math.sin(-geom.rotation);
    return {
      lx: cos * dx - sin * dy,
      ly: sin * dx + cos * dy
    };
  };

  // Build list of elements with bounding info for hit-testing and alignment
  const getElements = useCallback(() => {
    const els = [];

    // Face oval
    const geom = getFaceGeom();
    if (geom) {
      els.push({
        type: 'face',
        centerX: geom.cx, centerY: geom.ovalCY,
        top: geom.ovalCY - geom.ry, bottom: geom.ovalCY + geom.ry,
        left: geom.cx - geom.rx, right: geom.cx + geom.rx
      });
    }

    // Mascot
    if (formData.mascot_center_x != null && formData.mascot_center_y != null && formData.mascot_size != null) {
      const mx = pctToX(formData.mascot_center_x);
      const my = pctToY(formData.mascot_center_y);
      const ms = pctToX(formData.mascot_size);
      els.push({
        type: 'mascot',
        centerX: mx, centerY: my,
        top: my - ms / 2, bottom: my + ms / 2,
        left: mx - ms / 2, right: mx + ms / 2,
        halfSize: ms / 2
      });
    }

    // Text fields
    (formData.text_fields || []).forEach((tf, i) => {
      if (tf.center_x != null && tf.center_y != null) {
        const tx = pctToX(tf.center_x);
        const ty = pctToY(tf.center_y);
        const left = pctToX(tf.bound_x_left || 0);
        const right = pctToX(tf.bound_x_right || 1);
        els.push({
          type: 'text', index: i,
          centerX: tx, centerY: ty,
          top: ty - 12, bottom: ty + 12,
          left, right
        });
      }
    });

    // Collage photos
    (formData.collage_photos || []).forEach((photo, i) => {
      if (photo.x != null && photo.y != null && photo.width != null && photo.height != null) {
        const px = pctToX(photo.x);
        const py = pctToY(photo.y);
        const pw = pctToX(photo.width);
        const ph = pctToY(photo.height);
        els.push({
          type: 'collage_photo', index: i,
          centerX: px + pw / 2, centerY: py + ph / 2,
          top: py, bottom: py + ph,
          left: px, right: px + pw,
          width: pw, height: ph
        });
      }
    });

    return els;
  }, [formData, previewW, previewH, getFaceGeom]);

  // Hit test: what's under the cursor?
  const hitTest = useCallback((mx, my) => {
    const els = getElements();
    const geom = getFaceGeom();

    // Face handles first (rotation handle, resize handles) — only when face exists
    if (geom) {
      const { lx, ly } = toLocalFace(mx, my, geom);

      // Rotation handle: positioned above the oval at (0, -ry - ROTATE_HANDLE_OFFSET)
      const rotHandleY = -geom.ry - ROTATE_HANDLE_OFFSET;
      if (Math.abs(lx) <= FACE_HANDLE + 2 && Math.abs(ly - rotHandleY) <= FACE_HANDLE + 2) {
        return { type: 'face', action: 'rotate' };
      }

      // Top resize handle: at (0, -ry)
      if (Math.abs(lx) <= FACE_HANDLE + 4 && Math.abs(ly - (-geom.ry)) <= FACE_HANDLE) {
        return { type: 'face', action: 'resize-top' };
      }

      // Bottom resize handle: at (0, +ry)
      if (Math.abs(lx) <= FACE_HANDLE + 4 && Math.abs(ly - geom.ry) <= FACE_HANDLE) {
        return { type: 'face', action: 'resize-bottom' };
      }

      // Left/right resize handles: at (-rx, 0) and (+rx, 0)
      if (Math.abs(lx - (-geom.rx)) <= FACE_HANDLE && Math.abs(ly) <= FACE_HANDLE + 4) {
        return { type: 'face', action: 'resize-side' };
      }
      if (Math.abs(lx - geom.rx) <= FACE_HANDLE && Math.abs(ly) <= FACE_HANDLE + 4) {
        return { type: 'face', action: 'resize-side' };
      }
    }

    // Mascot: corner handles for resize, body for move
    for (const el of els) {
      if (el.type === 'mascot') {
        const hs = el.halfSize;
        // Corner resize handles
        const corners = [
          { x: el.left, y: el.top }, { x: el.right, y: el.top },
          { x: el.left, y: el.bottom }, { x: el.right, y: el.bottom }
        ];
        for (const c of corners) {
          if (Math.abs(mx - c.x) <= FACE_HANDLE + 2 && Math.abs(my - c.y) <= FACE_HANDLE + 2) {
            return { type: 'mascot', action: 'resize' };
          }
        }
        // Body move
        if (mx >= el.left - 4 && mx <= el.right + 4 && my >= el.top - 4 && my <= el.bottom + 4) {
          return { type: 'mascot', action: 'move' };
        }
      }
    }

    // Collage photos: corner handles for resize, body for move
    // Iterate in reverse so topmost (last) photo is selected first
    for (let ci = els.length - 1; ci >= 0; ci--) {
      const el = els[ci];
      if (el.type !== 'collage_photo') continue;
      const HANDLE = FACE_HANDLE;
      // Corner resize handles
      const corners = [
        { x: el.left, y: el.top, corner: 'tl' },
        { x: el.right, y: el.top, corner: 'tr' },
        { x: el.left, y: el.bottom, corner: 'bl' },
        { x: el.right, y: el.bottom, corner: 'br' }
      ];
      for (const c of corners) {
        if (Math.abs(mx - c.x) <= HANDLE + 2 && Math.abs(my - c.y) <= HANDLE + 2) {
          return { type: 'collage_photo', index: el.index, action: 'resize', corner: c.corner };
        }
      }
      // Edge resize handles
      if (Math.abs(mx - el.left) <= HANDLE && my > el.top + HANDLE && my < el.bottom - HANDLE) {
        return { type: 'collage_photo', index: el.index, action: 'resize-edge', edge: 'left' };
      }
      if (Math.abs(mx - el.right) <= HANDLE && my > el.top + HANDLE && my < el.bottom - HANDLE) {
        return { type: 'collage_photo', index: el.index, action: 'resize-edge', edge: 'right' };
      }
      if (Math.abs(my - el.top) <= HANDLE && mx > el.left + HANDLE && mx < el.right - HANDLE) {
        return { type: 'collage_photo', index: el.index, action: 'resize-edge', edge: 'top' };
      }
      if (Math.abs(my - el.bottom) <= HANDLE && mx > el.left + HANDLE && mx < el.right - HANDLE) {
        return { type: 'collage_photo', index: el.index, action: 'resize-edge', edge: 'bottom' };
      }
      // Body move
      if (mx >= el.left && mx <= el.right && my >= el.top && my <= el.bottom) {
        return { type: 'collage_photo', index: el.index, action: 'move' };
      }
    }

    // Check text field edges (for resize), then centers (for move)
    for (const el of els) {
      if (el.type === 'text') {
        if (my >= el.top - 4 && my <= el.bottom + 4) {
          if (Math.abs(mx - el.left) <= EDGE_GRAB) {
            return { type: 'text', index: el.index, action: 'resize-left' };
          }
          if (Math.abs(mx - el.right) <= EDGE_GRAB) {
            return { type: 'text', index: el.index, action: 'resize-right' };
          }
          if (mx >= el.left && mx <= el.right) {
            return { type: 'text', index: el.index, action: 'move' };
          }
        }
      }
    }

    // Face oval body (move) — check in local space for rotated oval
    if (geom) {
      const { lx, ly } = toLocalFace(mx, my, geom);
      const nx = lx / (geom.rx + 8);
      const ny = ly / (geom.ry + 8);
      if (nx * nx + ny * ny <= 1) {
        return { type: 'face', action: 'move' };
      }
    }

    return null;
  }, [getElements, getFaceGeom]);

  // Compute alignment guides during drag
  const computeGuides = useCallback((draggedEl, newCenterX, newCenterY, newLeft, newRight) => {
    const guides = [];
    const thresh = SNAP_PX;
    let snappedX = null;
    let snappedY = null;

    // Canvas center guides
    const canvasCenterX = previewW / 2;
    const canvasCenterY = previewH / 2;

    if (Math.abs(newCenterX - canvasCenterX) <= thresh) {
      guides.push({ axis: 'vertical', pos: canvasCenterX });
      snappedX = canvasCenterX;
    }
    if (Math.abs(newCenterY - canvasCenterY) <= thresh) {
      guides.push({ axis: 'horizontal', pos: canvasCenterY });
      snappedY = canvasCenterY;
    }

    // Element-to-element alignment
    const els = getElements();
    for (const el of els) {
      // Skip self
      if (draggedEl.type === el.type && draggedEl.index === el.index) continue;
      if (draggedEl.type === el.type && draggedEl.type === 'face') continue;

      // Center X alignment
      if (snappedX === null && Math.abs(newCenterX - el.centerX) <= thresh) {
        guides.push({ axis: 'vertical', pos: el.centerX });
        snappedX = el.centerX;
      }

      // Center Y alignment
      if (snappedY === null && Math.abs(newCenterY - el.centerY) <= thresh) {
        guides.push({ axis: 'horizontal', pos: el.centerY });
        snappedY = el.centerY;
      }

      // Edge alignment (text fields only)
      if (draggedEl.type === 'text' && el.type === 'text' && newLeft !== undefined) {
        if (Math.abs(newLeft - el.left) <= thresh) {
          guides.push({ axis: 'vertical', pos: el.left });
        }
        if (Math.abs(newRight - el.right) <= thresh) {
          guides.push({ axis: 'vertical', pos: el.right });
        }
      }
    }

    return { guides, snappedX, snappedY };
  }, [getElements, previewW, previewH]);

  const getCursor = useCallback((mx, my) => {
    const getCursorForHit = (hit) => {
      if (!hit) return 'default';
      if (hit.action === 'rotate') return 'crosshair';
      if (hit.action === 'resize-top' || hit.action === 'resize-bottom') return 'ns-resize';
      if (hit.action === 'resize-side') return 'nwse-resize';
      if (hit.action === 'resize') {
        if (hit.corner === 'tl' || hit.corner === 'br') return 'nwse-resize';
        return 'nesw-resize';
      }
      if (hit.action === 'resize-edge') {
        if (hit.edge === 'left' || hit.edge === 'right') return 'ew-resize';
        return 'ns-resize';
      }
      if (hit.action === 'resize-left' || hit.action === 'resize-right') return 'ew-resize';
      if (hit.action === 'move') return dragRef.current ? 'grabbing' : 'grab';
      return dragRef.current ? 'grabbing' : 'grab';
    };
    if (dragRef.current) return getCursorForHit(dragRef.current);
    return getCursorForHit(hitTest(mx, my));
  }, [hitTest]);

  const getMousePos = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / zoom,
      y: (e.clientY - rect.top) / zoom
    };
  };

  const onMouseDown = useCallback((e) => {
    const pos = getMousePos(e);
    const hit = hitTest(pos.x, pos.y);
    if (!hit) {
      setSelectedElement(null);
      return;
    }

    // Select the clicked element (type + index for text fields)
    setSelectedElement({ type: hit.type, index: hit.index });
    e.preventDefault();
    const extra = {};

    // For rotation, record the starting angle from oval center to mouse
    if (hit.action === 'rotate') {
      const geom = getFaceGeom();
      if (geom) {
        extra.startAngle = Math.atan2(pos.y - geom.ovalCY, pos.x - geom.cx) * 180 / Math.PI;
        extra.startRotation = formData.rotation || 0;
        extra.ovalCX = geom.cx;
        extra.ovalCY = geom.ovalCY;
      }
    }

    // For resize, record starting distance
    if (hit.action === 'resize-top' || hit.action === 'resize-bottom' || hit.action === 'resize-side') {
      const geom = getFaceGeom();
      if (geom) {
        extra.startFaceHeight = formData.face_height;
        extra.ovalCX = geom.cx;
        extra.ovalCY = geom.ovalCY;
        extra.startRotation = geom.rotation; // in radians
      }
    }

    const state = { ...hit, ...extra, startX: pos.x, startY: pos.y, startFormData: formData };
    setDragState(state);
    dragRef.current = state;
  }, [hitTest, formData, getFaceGeom]);

  const onMouseMove = useCallback((e) => {
    const pos = getMousePos(e);
    const ds = dragRef.current;

    if (!ds) {
      // Just update hover state + cursor
      const hit = hitTest(pos.x, pos.y);
      setHoveredElement(hit);
      e.currentTarget.style.cursor = getCursor(pos.x, pos.y);
      setAlignmentGuides([]);
      return;
    }

    e.preventDefault();
    e.currentTarget.style.cursor = getCursor(pos.x, pos.y);

    const dx = pos.x - ds.startX;
    const dy = pos.y - ds.startY;
    const fd = ds.startFormData;

    let newFormData = { ...fd };

    if (ds.type === 'face' && ds.action === 'move') {
      const origCX = pctToX(fd.center_x);
      const origEY = pctToY(fd.eyes_y);
      const newCX = origCX + dx;
      const newEY = origEY + dy;
      const { guides, snappedX, snappedY } = computeGuides(
        ds, newCX, newEY, undefined, undefined
      );
      const finalCX = snappedX !== null ? snappedX : newCX;
      const finalEY = snappedY !== null ? snappedY : newEY;
      newFormData.center_x = Math.max(0, Math.min(1, xToPct(finalCX)));
      newFormData.eyes_y = Math.max(0, Math.min(1, yToPct(finalEY)));
      setAlignmentGuides(guides);
    }
    else if (ds.type === 'face' && ds.action === 'rotate') {
      // Use captured center from drag start to avoid drift
      if (ds.ovalCX != null && ds.ovalCY != null) {
        const currentAngle = Math.atan2(pos.y - ds.ovalCY, pos.x - ds.ovalCX) * 180 / Math.PI;
        const deltaAngle = currentAngle - ds.startAngle;
        let newRotation = ds.startRotation + deltaAngle;
        // Snap to 0 when close
        if (Math.abs(newRotation) < 2) newRotation = 0;
        // Clamp to -180..180
        while (newRotation > 180) newRotation -= 360;
        while (newRotation < -180) newRotation += 360;
        const rounded = Math.round(newRotation * 10) / 10;
        newFormData.rotation = rounded;
        // Keep ref photo rotation in sync
        newFormData.ref_rotation = rounded;
      }
      setAlignmentGuides([]);
    }
    else if (ds.type === 'face' && (ds.action === 'resize-top' || ds.action === 'resize-bottom')) {
      const cos = Math.cos(-ds.startRotation);
      const sin = Math.sin(-ds.startRotation);
      const localDX = cos * (pos.x - ds.ovalCX) - sin * (pos.y - ds.ovalCY);
      const localDY = sin * (pos.x - ds.ovalCX) + cos * (pos.y - ds.ovalCY);
      const newRy = Math.abs(localDY);
      const newFh = newRy * 2;
      const newFaceHeight = Math.max(0.02, Math.min(1, yToPct(newFh)));
      newFormData.face_height = Math.round(newFaceHeight * 1000) / 1000;
      setAlignmentGuides([]);
    }
    else if (ds.type === 'face' && ds.action === 'resize-side') {
      const cos = Math.cos(-ds.startRotation);
      const sin = Math.sin(-ds.startRotation);
      const localDX = cos * (pos.x - ds.ovalCX) - sin * (pos.y - ds.ovalCY);
      const newRx = Math.abs(localDX);
      const newFh = newRx / 0.35;
      const newFaceHeight = Math.max(0.02, Math.min(1, yToPct(newFh)));
      newFormData.face_height = Math.round(newFaceHeight * 1000) / 1000;
      setAlignmentGuides([]);
    }
    else if (ds.type === 'text') {
      const tf = fd.text_fields[ds.index];
      const fields = [...(fd.text_fields || [])];

      if (ds.action === 'move') {
        const origCX = pctToX(tf.center_x);
        const origCY = pctToY(tf.center_y);
        const newCX = origCX + dx;
        const newCY = origCY + dy;
        // Move bounds with center
        const origLeft = pctToX(tf.bound_x_left || 0);
        const origRight = pctToX(tf.bound_x_right || 1);
        const newLeft = origLeft + dx;
        const newRight = origRight + dx;
        const { guides, snappedX, snappedY } = computeGuides(
          ds, newCX, newCY, newLeft, newRight
        );
        const snapDx = snappedX !== null ? snappedX - newCX : 0;
        fields[ds.index] = {
          ...tf,
          center_x: Math.max(0, Math.min(1, xToPct(newCX + snapDx))),
          center_y: Math.max(0, Math.min(1, yToPct(snappedY !== null ? snappedY : newCY))),
          bound_x_left: Math.max(0, Math.min(1, xToPct(newLeft + snapDx))),
          bound_x_right: Math.max(0, Math.min(1, xToPct(newRight + snapDx)))
        };
        setAlignmentGuides(guides);
      }
      else if (ds.action === 'resize-left') {
        const origLeft = pctToX(tf.bound_x_left || 0);
        const newLeft = Math.max(0, Math.min(pctToX(tf.bound_x_right || 1) - 20, origLeft + dx));
        fields[ds.index] = { ...tf, bound_x_left: xToPct(newLeft) };
        setAlignmentGuides([]);
      }
      else if (ds.action === 'resize-right') {
        const origRight = pctToX(tf.bound_x_right || 1);
        const newRight = Math.max(pctToX(tf.bound_x_left || 0) + 20, Math.min(previewW, origRight + dx));
        fields[ds.index] = { ...tf, bound_x_right: xToPct(newRight) };
        setAlignmentGuides([]);
      }

      newFormData.text_fields = fields;
    }
    else if (ds.type === 'mascot' && ds.action === 'move') {
      const origCX = pctToX(fd.mascot_center_x);
      const origCY = pctToY(fd.mascot_center_y);
      const newCX = origCX + dx;
      const newCY = origCY + dy;
      const { guides, snappedX, snappedY } = computeGuides(ds, newCX, newCY);
      newFormData.mascot_center_x = Math.max(0, Math.min(1, xToPct(snappedX !== null ? snappedX : newCX)));
      newFormData.mascot_center_y = Math.max(0, Math.min(1, yToPct(snappedY !== null ? snappedY : newCY)));
      setAlignmentGuides(guides);
    }
    else if (ds.type === 'mascot' && ds.action === 'resize') {
      // Resize: distance from center to mouse determines half-size
      const mcx = pctToX(fd.mascot_center_x);
      const mcy = pctToY(fd.mascot_center_y);
      const distX = Math.abs(pos.x - mcx);
      const distY = Math.abs(pos.y - mcy);
      const halfSize = Math.max(distX, distY);
      const newSize = Math.max(0.02, Math.min(1, xToPct(halfSize * 2)));
      newFormData.mascot_size = Math.round(newSize * 1000) / 1000;
      setAlignmentGuides([]);
    }
    else if (ds.type === 'collage_photo' && ds.action === 'move') {
      const photos = [...(fd.collage_photos || [])];
      const photo = photos[ds.index];
      if (photo) {
        const origX = pctToX(photo.x);
        const origY = pctToY(photo.y);
        const newX = origX + dx;
        const newY = origY + dy;
        const centerX = newX + pctToX(photo.width) / 2;
        const centerY = newY + pctToY(photo.height) / 2;
        const { guides, snappedX, snappedY } = computeGuides(ds, centerX, centerY);
        const snapDx = snappedX !== null ? snappedX - centerX : 0;
        const snapDy = snappedY !== null ? snappedY - centerY : 0;
        photos[ds.index] = {
          ...photo,
          x: Math.max(0, Math.min(1, xToPct(newX + snapDx))),
          y: Math.max(0, Math.min(1, yToPct(newY + snapDy)))
        };
        newFormData.collage_photos = photos;
        setAlignmentGuides(guides);
      }
    }
    else if (ds.type === 'collage_photo' && ds.action === 'resize') {
      const photos = [...(fd.collage_photos || [])];
      const photo = photos[ds.index];
      if (photo) {
        const origX = pctToX(photo.x);
        const origY = pctToY(photo.y);
        const origW = pctToX(photo.width);
        const origH = pctToY(photo.height);
        const aspectRatio = origW / (origH || 1);
        let newX = origX, newY = origY, newW = origW, newH = origH;

        // Corner resize preserves aspect ratio
        const corner = ds.corner;
        if (corner === 'br') {
          newW = Math.max(20, pctToX(photo.width) + dx);
          newH = newW / aspectRatio;
        } else if (corner === 'bl') {
          const dw = -dx;
          newW = Math.max(20, origW + dw);
          newH = newW / aspectRatio;
          newX = origX + origW - newW;
        } else if (corner === 'tr') {
          newW = Math.max(20, origW + dx);
          newH = newW / aspectRatio;
          newY = origY + origH - newH;
        } else if (corner === 'tl') {
          const dw = -dx;
          newW = Math.max(20, origW + dw);
          newH = newW / aspectRatio;
          newX = origX + origW - newW;
          newY = origY + origH - newH;
        }

        photos[ds.index] = {
          ...photo,
          x: Math.max(0, Math.min(1, xToPct(newX))),
          y: Math.max(0, Math.min(1, yToPct(newY))),
          width: Math.max(0.02, Math.min(1, xToPct(newW))),
          height: Math.max(0.02, Math.min(1, yToPct(newH)))
        };
        newFormData.collage_photos = photos;
        setAlignmentGuides([]);
      }
    }
    else if (ds.type === 'collage_photo' && ds.action === 'resize-edge') {
      // Edge resize also locks aspect ratio
      const photos = [...(fd.collage_photos || [])];
      const photo = photos[ds.index];
      if (photo) {
        const origX = pctToX(photo.x);
        const origY = pctToY(photo.y);
        const origW = pctToX(photo.width);
        const origH = pctToY(photo.height);
        const aspectRatio = origW / (origH || 1);
        let newX = origX, newY = origY, newW = origW, newH = origH;

        if (ds.edge === 'right') {
          newW = Math.max(20, origW + dx);
          newH = newW / aspectRatio;
        } else if (ds.edge === 'left') {
          newW = Math.max(20, origW - dx);
          newH = newW / aspectRatio;
          newX = origX + origW - newW;
        } else if (ds.edge === 'bottom') {
          newH = Math.max(20, origH + dy);
          newW = newH * aspectRatio;
        } else if (ds.edge === 'top') {
          newH = Math.max(20, origH - dy);
          newW = newH * aspectRatio;
          newY = origY + origH - newH;
        }

        photos[ds.index] = {
          ...photo,
          x: Math.max(0, Math.min(1, xToPct(newX))),
          y: Math.max(0, Math.min(1, yToPct(newY))),
          width: Math.max(0.02, Math.min(1, xToPct(newW))),
          height: Math.max(0.02, Math.min(1, yToPct(newH)))
        };
        newFormData.collage_photos = photos;
        setAlignmentGuides([]);
      }
    }

    onChange(newFormData);
  }, [hitTest, getCursor, computeGuides, onChange, previewW, previewH, getFaceGeom]);

  const onMouseUp = useCallback((e) => {
    if (dragRef.current) {
      dragRef.current = null;
      setDragState(null);
      setAlignmentGuides([]);
      onCommit();
    }
  }, [onCommit]);

  const onMouseLeave = useCallback(() => {
    if (dragRef.current) {
      dragRef.current = null;
      setDragState(null);
      setAlignmentGuides([]);
      onCommit();
    }
    setHoveredElement(null);
  }, [onCommit]);

  const clearSelection = useCallback(() => setSelectedElement(null), []);

  return {
    handlers: { onMouseDown, onMouseMove, onMouseUp, onMouseLeave },
    alignmentGuides,
    hoveredElement,
    selectedElement,
    clearSelection,
    isDragging: dragRef.current !== null
  };
};

export default useCanvasDrag;
